import { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    const userData = localStorage.getItem('userData')

    if (token && userData) {
      try {
        setUser(JSON.parse(userData))
      } catch (error) {
        console.error('Error parsing user data:', error)
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('userData')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    setLoading(true)
    try {
      const response = await api.post('/auth/login', { email, password })
      const { user: userData, tokens } = response.data

      const userObj = {
        id: userData.id,
        email: userData.email,
        name: userData.name || userData.firstName || email.split('@')[0],
        role: userData.role,
        firstName: userData.firstName,
        lastName: userData.lastName,
        rewardsBalance: userData.rewardsBalance,
        manufacturerId: userData.manufacturerId || null,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || email)}&background=1E4D40&color=A3E635`
      }

      setUser(userObj)
      localStorage.setItem('accessToken', tokens.accessToken)
      localStorage.setItem('refreshToken', tokens.refreshToken)
      localStorage.setItem('userData', JSON.stringify(userObj))
      toast.success('Login successful!')
      return { user: userObj, tokens }
    } catch (error) {
      const msg = error.response?.data?.error || 'Login failed'
      toast.error(msg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    setLoading(true)
    try {
      const response = await api.post('/auth/register', {
        email: userData.email,
        password: userData.password,
        firstName: userData.name?.split(' ')[0] || userData.firstName || '',
        lastName: userData.name?.split(' ').slice(1).join(' ') || userData.lastName || '',
        phone: userData.phone || '',
        role: userData.role || 'consumer',
        companyName: userData.companyName || '',
      })
      const { user: newUser, tokens } = response.data

      const userObj = {
        id: newUser.id,
        email: newUser.email,
        name: newUser.name || newUser.firstName || userData.email.split('@')[0],
        role: newUser.role,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        manufacturerId: newUser.manufacturerId || null,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newUser.name || userData.email)}&background=1E4D40&color=A3E635`
      }

      setUser(userObj)
      localStorage.setItem('accessToken', tokens.accessToken)
      localStorage.setItem('refreshToken', tokens.refreshToken)
      localStorage.setItem('userData', JSON.stringify(userObj))
      toast.success('Registration successful!')
      return { user: userObj, tokens }
    } catch (error) {
      const msg = error.response?.data?.error || 'Registration failed'
      toast.error(msg)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      await api.post('/auth/logout', { refreshToken }).catch(() => {})
    } catch (e) {
      // ignore logout API errors
    }
    setUser(null)
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userData')
    toast.success('Logged out successfully')
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isManufacturer: user?.role === 'manufacturer',
    manufacturerId: user?.manufacturerId || null,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
